import React, { useState, useMemo, useCallback } from "react";
import { SafeImage, FOOD_PLACEHOLDER } from "../components/common/SafeImage";
import { useFavicon } from "../hooks/useFavicon";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMenuByKitchenId, getKitchenDetails } from "../services/kitchen.service";
// ✅ IMPORT TYPES FROM SOURCE
import { 
  Menu, 
  MenuItem, 
  CartItem, 
  Kitchen, 
  CustomizationLevel, 
  CustomizationOption,
  SelectedCustomization // ✅ Added this import
} from "../types/index"; 
import { useCartStore } from "../store/cart.store";
import CircleLogo from "../components/CircleLogo";
import { Button } from "../components/common/Button";
import { toast } from "react-hot-toast";
import { collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import AnnouncementImages from "../components/announce/AnnouncementImages";

// --- LOCAL UTILITY TYPES ---
type SelectionMap = Record<string, string[]>;

// ✅ FIXED: Helper function now produces the GLOBAL Type structure
// Flattening hierarchy: { level: "Size", option: "Large" } -> { label: "Size", value: "Large" }
function buildSelectedCustomizations(
  pendingItem: MenuItem,
  selections: Record<string, string[]>
): SelectedCustomization[] {
  const results: SelectedCustomization[] = [];

  const traverse = (levels: CustomizationLevel[]) => {
    levels.forEach(level => {
      if (level.isActive === false) return;

      const selectedKeys = selections[level.key] || [];
      
      // Find the full option objects for the selected keys
      const selectedOptions = level.options.filter(opt => 
        opt.isActive !== false && selectedKeys.includes(opt.key)
      );

      // Create a flat entry for each selected option
      selectedOptions.forEach(opt => {
        results.push({
          label: level.label,       // e.g. "Size"
          value: opt.name,          // e.g. "Large"
          price: opt.extraPrice || 0,
          type: level.label.toLowerCase().includes("addon") ? "addon" : "variant"
        });

        // Recursively handle nested customizations
        if (opt.customizations) {
          traverse(opt.customizations);
        }
      });
    });
  };

  if (pendingItem.customizations) traverse(pendingItem.customizations);
  return results;
}

const getBaseDisplayPrice = (item: MenuItem): number => {
  return item.basePrice ?? item.price ?? 0;
};

// --- COMPONENT ---

const KitchenMenuRegular: React.FC = () => {
  const { kitchenId = "" } = useParams<{ kitchenId: string }>();
  const navigate = useNavigate();
  
  // Modal State
  const [pendingItem, setPendingItem] = useState<MenuItem | null>(null);
  const [selections, setSelections] = useState<SelectionMap>({});
  const [quantity, setQuantity] = useState(1);

  // Cart Store
  const { items: cart, addItem: addToCart, totalPrice } = useCartStore();

  // 1. Queries
  const { 
    data: menu, 
    isLoading: isMenuLoading, 
    isError: isMenuError, 
    error: menuError 
  } = useQuery<Menu, Error>(
    ["menu", kitchenId],
    () => getMenuByKitchenId(kitchenId),
    { enabled: !!kitchenId, retry: 2 }
  );

  const specialsQuery = useQuery<any[]>(
    ["kitchenSpecialsLive", kitchenId],
    async () => {
      // single-field query (no composite index); expiry filtered here
      const snap = await getDocs(query(collection(db, "special_announcements"), where("kitchenId", "==", kitchenId)));
      const now = Timestamp.now().toMillis();
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((s) => s.active !== false && s.expiresAt?.toMillis && s.expiresAt.toMillis() > now);
    },
    { enabled: !!kitchenId }
  );
  const liveSpecials: any[] = specialsQuery.data || [];

  const { data: kitchenDetails, isLoading: isKitchenLoading } = useQuery<Kitchen | null>(
    ["kitchenDetails", kitchenId],
    () => getKitchenDetails(kitchenId),
    { enabled: !!kitchenId }
  );

  useFavicon(kitchenDetails?.logoUrl);

  // 2. Data Grouping
  const groupedMenu = useMemo(() => {
    if (!menu?.menuItems) return {};
    
    const groups: Record<string, MenuItem[]> = {};
    
    // Items stored as a map are keyed by id and often lack an `id` field
    // (Manage Menu never writes one). Use the map key so every item has a
    // unique id; otherwise the dedupe below (undefined === undefined)
    // collapses all id-less items in a category into the first one.
    const items: MenuItem[] = Array.isArray(menu.menuItems)
      ? menu.menuItems.map((item, idx) => ({ ...item, id: item.id || `item_${idx}` }))
      : Object.entries(menu.menuItems).map(([key, item]) => ({ ...item, id: item.id || key }));

    items.forEach((item) => {
      if (item.isAvailable === false) return;

      // Primary category only — avoid listing the same dish under dinner AND special
      const itemCats = (item.categories && item.categories.length > 0) 
        ? item.categories 
        : ["Main Course"];
      const primaryCat = itemCats[0] || "Main Course";
      if (!groups[primaryCat]) groups[primaryCat] = [];
      if (!groups[primaryCat].find(i => i.id === item.id)) {
        groups[primaryCat].push(item);
      }
    });

    return groups;
  }, [menu]);

  // 3. Image Layering Engine
  const activeLayers = useMemo(() => {
    if (!pendingItem) return [];
    
    const layers: string[] = [];

    // Layer 0: Base Item Image
    if (pendingItem.photoUrl) {
      layers.push(pendingItem.photoUrl);
    } else {
      layers.push(FOOD_PLACEHOLDER);
    }

    const traverse = (levels: CustomizationLevel[]) => {
      levels.forEach(level => {
        if (level.isActive === false) return;
        
        const selectedKeys = selections[level.key] || [];
        
        level.options.forEach(opt => {
          if (opt.isActive === false) return;

          if (selectedKeys.includes(opt.key)) {
            if (opt.photoUrl && opt.photoUrl.trim() !== "") {
              layers.push(opt.photoUrl);
            }
            if (opt.customizations) {
              traverse(opt.customizations);
            }
          }
        });
      });
    };

    if (pendingItem.customizations) {
      traverse(pendingItem.customizations);
    }

    return layers;
  }, [pendingItem, selections]);

  // 4. Handlers

  const openModal = (item: MenuItem) => {
    setPendingItem(item);
    setSelections({});
    setQuantity(1);
  };

  const handleOptionToggle = (levelKey: string, optionKey: string, isSingleSelect: boolean = true) => {
    setSelections(prev => {
      const levelSelections = prev[levelKey] || [];
      if (levelSelections.includes(optionKey)) {
        return { ...prev, [levelKey]: levelSelections.filter(k => k !== optionKey) };
      } else {
        return { 
          ...prev, 
          [levelKey]: isSingleSelect ? [optionKey] : [...levelSelections, optionKey] 
        };
      }
    });
  };

  const calculateTotal = useCallback(() => {
    if (!pendingItem) return 0;
    
    let total = pendingItem.basePrice || 0;

    const traverse = (levels: CustomizationLevel[]) => {
      levels.forEach(level => {
        if (level.isActive === false) return;

        const selectedKeys = selections[level.key] || [];
        
        level.options.forEach(opt => {
          if (opt.isActive === false) return;

          if (selectedKeys.includes(opt.key)) {
            total += opt.extraPrice || 0;
            if (opt.customizations) {
              traverse(opt.customizations);
            }
          }
        });
      });
    };

    if (pendingItem.customizations) {
      traverse(pendingItem.customizations);
    }

    return total * quantity;
  }, [pendingItem, selections, quantity]);

  const getOptionEffectivePrice = (
    level: CustomizationLevel,
    opt: CustomizationOption
  ): { delta: number; effective: number } => {
    const base = pendingItem?.basePrice || 0;
    const delta = opt.extraPrice || 0;
    const effective = base + delta;
    return { delta, effective };
  };

  const renderCustomizationLevels = (levels: CustomizationLevel[]) => {
    return levels.map(level => {
      if (level.isActive === false) return null;

      return (
        <div key={level.key} className="mb-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
            {level.label}
          </h4>
          <div className="space-y-3">
            {level.options.map(opt => {
              if (opt.isActive === false) return null;

              const isSelected = (selections[level.key] || []).includes(opt.key);

              return (
                <div key={opt.key}>
                  <div 
                    onClick={() => handleOptionToggle(level.key, opt.key, true)}
                    className={`
                      relative p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between
                      ${isSelected 
                        ? "border-orange-500 bg-orange-50 text-orange-900 shadow-sm" 
                        : "border-gray-100 hover:border-orange-200 hover:bg-gray-50"
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-5 h-5 rounded-full border flex items-center justify-center transition-colors
                        ${isSelected ? "border-orange-500 bg-orange-500" : "border-gray-300 bg-white"}
                      `}>
                        {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                      
                      {opt.photoUrl && (
                        <SafeImage src={opt.photoUrl} alt="" className="w-8 h-8 rounded object-cover bg-gray-200" />
                      )}
                      
                      <span className="font-medium">{opt.name}</span>
                    </div>
                    
                    {(() => {
                      const { delta, effective } = getOptionEffectivePrice(level, opt);
                      return (
                        <div className="text-right leading-tight">
                          {delta !== 0 && (
                            <div className="text-[9px] text-gray-500 font-medium">
                              {delta > 0 ? `+₹${delta.toFixed(2)}` : `₹${delta.toFixed(2)}`}
                            </div>
                          )}
                          <div className="text-xs sm:text-sm font-semibold text-gray-700">
                            ₹{effective.toFixed(2)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {isSelected && opt.customizations && opt.customizations.length > 0 && (
                    <div className="ml-6 mt-3 border-l-2 border-orange-200 pl-4">
                      {renderCustomizationLevels(opt.customizations)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const finalizeAddToCart = () => {
    if (!pendingItem) return;

    try {
      const finalPrice = calculateTotal();
      
      const selectionString = Object.entries(selections)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([k, v]) => `${k}:${v.sort().join(",")}`)
        .join("|");

      const deterministicId = `${kitchenId}_${pendingItem.id}_[${selectionString}]`;
      
      // ✅ Now returns correct Global Type structure (SelectedCustomization[])
      const selectedSpecs = buildSelectedCustomizations(pendingItem, selections);
      
      const cartItem: CartItem = {
        id: deterministicId,
        name: pendingItem.name,
        price: finalPrice, 
        quantity: quantity,
        category: pendingItem.categories?.[0] || "Custom",
        kitchenId,
        kitchenName: kitchenDetails?.kitchen_name || "Kitchen",
        photoUrl: activeLayers.length > 0 ? activeLayers[activeLayers.length - 1] : pendingItem.photoUrl,
        description: pendingItem.description,
        customizations: selectedSpecs,  
        // The server re-prices from these (menu key + chosen option keys); `price` is display only.
        menuItemId: pendingItem.id,
        selections: Object.fromEntries(Object.entries(selections).map(([k, v]) => [k, [...v]])),
      };

      addToCart(cartItem);
      setPendingItem(null);
      toast.success(`${quantity}x ${pendingItem.name} added!`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to add item");
    }
  };

  // --- RENDER ---

  if (isMenuLoading || isKitchenLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4" />
        <h2 className="text-xl font-bold text-gray-700">Loading Menu...</h2>
      </div>
    );
  }

  if (isMenuError || Object.keys(groupedMenu).length === 0) {
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-red-800 mb-2">Menu Unavailable</h3>
          <p className="text-gray-600 mb-4">
            {(menuError as Error)?.message || "No active items found for this kitchen."}
          </p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 pb-32">
      
      {/* Header */}
      <div className="flex items-center gap-6 mb-10 border-b border-gray-100 pb-8">
        <CircleLogo 
          src={kitchenDetails?.logoUrl || "/ruchira-logo.png"} 
          alt={kitchenDetails?.kitchen_name || "Kitchen"} 
          size={90} 
          className="shadow-lg border-4 border-white"
        />
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {kitchenDetails?.kitchen_name || menu?.name || "Kitchen Menu"}
          </h1>
          <div className="flex gap-2 mt-2 text-sm font-medium text-gray-500">
             <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Home Kitchen</span>
             <span>•</span>
             <span>{Object.keys(groupedMenu).join(", ")}</span>
          </div>
        </div>
      </div>

      {/* Today's special (from the kitchen's announcement) */}
      {liveSpecials.map((sp) => (
        <div key={sp.id} className="mb-8 bg-white rounded-2xl shadow-sm border border-orange-200 overflow-hidden">
          <AnnouncementImages images={sp.images?.length ? sp.images : [sp.imageUrl]} alt={sp.name} />
          <div className="p-4">
            <span className="text-[11px] font-bold uppercase tracking-wide text-orange-600">{sp.title || "Today's special"}</span>
            <h2 className="text-lg font-extrabold text-gray-900">{sp.name}{sp.price ? ` · ₹${sp.price}` : ""}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {sp.deliveryWindow ? `🛵 ${sp.deliveryWindow}` : ""}{sp.orderCutoff ? ` · ⏰ order by ${sp.orderCutoff}` : ""}
            </p>
          </div>
        </div>
      ))}

      {/* List View */}
      <div className="space-y-12">
        {Object.entries(groupedMenu).map(([category, items]) => (
          <div key={category} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-orange-50/80 px-6 py-4 border-b border-orange-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-orange-900">{category}</h3>
              <span className="text-xs font-bold bg-white text-orange-600 px-2 py-1 rounded shadow-sm">
                {items.length} ITEMS
              </span>
            </div>

            <div className="divide-y divide-gray-50">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="group p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-orange-50/30 transition-colors"
                >
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                    <SafeImage
                      src={item.photoUrl}
                      alt={item.name}
                      className="w-16 h-16 rounded-xl object-cover shadow-sm bg-gray-200 group-hover:scale-105 transition-transform shrink-0"
                    />
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-900 text-base sm:text-lg">{item.name}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                        {item.description || "Freshly prepared home-style goodness."}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 sm:pl-2">
                    <div className="text-base sm:text-lg font-bold text-gray-900 whitespace-nowrap">
                      <span className="text-xs text-gray-400 font-normal mr-1">from</span>
                      ₹{getBaseDisplayPrice(item)}
                    </div>
                    <Button
                      variant="primary"
                      size="md"
                      className="min-h-[44px] px-5 sm:px-6 rounded-full shadow-orange-200 shadow-lg"
                      onClick={() => openModal(item)}
                    >
                      ADD +
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {pendingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom-10">
            
            {/* Hero Layered Image */}
            <div className="relative h-56 sm:h-64 w-full shrink-0 bg-gray-100 overflow-hidden">
              {activeLayers.map((url, index) => (
                <SafeImage 
                  key={`${url}-${index}`}
                  src={url} 
                  alt="" 
                  className={`
                    w-full h-full object-cover transition-opacity duration-500
                    ${index === 0 ? "relative z-0" : "absolute inset-0 z-10"} 
                  `}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent z-20 pointer-events-none" />
              
              <button 
                onClick={() => setPendingItem(null)}
                aria-label="Close"
                className="absolute top-4 right-4 min-w-[44px] min-h-[44px] w-11 h-11 flex items-center justify-center bg-white/20 backdrop-blur-md hover:bg-white/40 text-white rounded-full p-2 transition-colors z-30"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="absolute bottom-4 left-5 right-5 text-white z-30">
                <h3 className="text-2xl font-bold leading-tight shadow-black drop-shadow-md">
                  {pendingItem.name}
                </h3>
                <p className="text-white/80 text-sm mt-1 line-clamp-1">Customize your meal</p>
              </div>
            </div>

            {/* Options Body */}
            <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-gray-200">
              {(!pendingItem.customizations || pendingItem.customizations.length === 0) ? (
                <div className="text-center py-10 text-gray-500">No customizations available.</div>
              ) : (
                renderCustomizationLevels(pendingItem.customizations)
              )}
              
              {/* Quantity */}
              <div className="mt-8 pt-6 border-t border-dashed border-gray-200">
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                   <span className="font-bold text-gray-700">Quantity</span>
                   <div className="flex items-center gap-6">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-white border border-gray-200 text-xl font-bold hover:bg-gray-100 transition flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="text-xl font-bold text-gray-900 w-8 text-center">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-orange-500 text-white text-xl font-bold hover:bg-orange-600 shadow-md transition flex items-center justify-center"
                      >
                        +
                      </button>
                   </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-white shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
               <Button 
                 variant="primary" 
                 className="w-full text-lg py-4 shadow-xl shadow-orange-200 flex justify-between items-center px-6"
                 onClick={finalizeAddToCart}
               >
                 <span>Add to Cart</span>
                 <span className="bg-white/20 px-3 py-1 rounded text-base">
                  ₹{calculateTotal().toFixed(2)}
                 </span>
               </Button>
            </div>

          </div>
        </div>
      )}

      {/* Floating Cart */}
      {cart.length > 0 && !pendingItem && (
        <div className="fixed bottom-6 left-4 right-4 max-w-md mx-auto z-40 animate-in slide-in-from-bottom-20">
          <div 
            onClick={() => navigate("/cart")}
            className="bg-gray-900 text-white p-4 rounded-2xl shadow-2xl flex justify-between items-center cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <div className="flex flex-col pl-2">
              <span className="font-bold text-lg">{cart.length} ITEMS</span>
              <span className="text-sm text-gray-400">₹{totalPrice().toFixed(2)} total</span>
            </div>
            <div className="bg-white/20 px-4 py-2 rounded-xl font-bold text-white flex items-center gap-2">
              View Cart <span className="text-xl">→</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default KitchenMenuRegular;